// src/modules/admin/AuthorsList.js

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const AuthorsList = () => {
  const [authors, setAuthors] = useState([]);
  const [filteredAuthors, setFilteredAuthors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState(useLocation().state?.defaultFilter || 'all');
  const [page, setPage] = useState(1);
  const perPage = 5;
  const API = process.env.REACT_APP_API_BASE_URL;

  const [approvedCount, setApprovedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);

  const [isSending, setIsSending] = useState(false); // 🆕 new state for send email status

  useEffect(() => {
    const fetchAuthors = async () => {
      try {
        const res = await axios.get(`${API}/api/author/authors`, {
          params: { status: filter === 'all' ? '' : filter },
        });
        const data = res.data || [];
        setAuthors(data);
        setApprovedCount(data.filter(a => a.isApproved === 1).length);
        setPendingCount(data.filter(a => a.isApproved === 2).length);
        setRejectedCount(data.filter(a => a.isApproved === 3).length);
        setPage(1);
      } catch (err) {
        console.error(err);
      }
    };

    fetchAuthors();
  }, [filter, API]);

  useEffect(() => {
    const lowercased = searchTerm.toLowerCase();
    const filtered = authors.filter(author =>
      author.AuthorName.toLowerCase().includes(lowercased) ||
      author.EmailID.toLowerCase().includes(lowercased)
    );
    setFilteredAuthors(filtered);
    setPage(1);
  }, [searchTerm, authors]);

  const paginatedAuthors = filteredAuthors.slice((page - 1) * perPage, page * perPage);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleFilterChange = (newFilter) => setFilter(newFilter);
  const handlePageChange = (newPage) => setPage(newPage);

  const getAuthorStatus = (author) => {
    if (author.isApproved === 1) return 'Approved';
    if (author.isApproved === 2) return 'Pending';
    if (author.isApproved === 3) return 'Rejected';
    return 'Not Approved';
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Authors List', 14, 20);

    const tableColumn = ['Author Name', 'Email', 'Status'];
    const tableRows = filteredAuthors.map(author => [
      author.AuthorName,
      author.EmailID,
      getAuthorStatus(author),
    ]);

    autoTable(doc, {
      startY: 30,
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 10, cellPadding: 2 },
      theme: 'grid',
    });

    doc.save('authors_list.pdf');
  };

  const exportToExcel = () => {
    const data = filteredAuthors.map(author => ({
      'Author Name': author.AuthorName,
      Email: author.EmailID,
      Status: getAuthorStatus(author),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Authors');
    XLSX.writeFile(workbook, 'authors_list.xlsx');
  };

  // 🆕 Function to send email to all approved authors
  const handleSendEmails = async () => {
    if (!window.confirm('Are you sure you want to send the email to all approved authors?')) return;

    try {
      setIsSending(true);

      const subject = '📘 Final Paper Submission Format - IWWA Convention 2026';
      const htmlContent = `
        <p>We are pleased to share the official format for final paper submission for the IWWA Convention 2026.</p>
        <p>Please find the attached document for detailed instructions.</p>
        <p>Thank you for your contribution!</p>
      `;

      const res = await axios.post(`${API}/api/author/send-to-approved`, { subject, htmlContent });
      alert(res.data.message || 'Emails sent successfully!');
    } catch (err) {
      console.error('Error sending emails:', err);
      alert('Failed to send emails. Please check the server logs.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="container my-4 ml-2">
      <h3 className="page-title mb-4">Authors List</h3>

      {/* Status Counts */}
      {/* <div className="mb-3 d-flex flex-wrap gap-3">
        <div className="badge bg-success text-white p-2">Approved: {approvedCount}</div>
        <div className="badge bg-warning text-dark p-2">Pending: {pendingCount}</div>
        <div className="badge bg-danger text-white p-2">Rejected: {rejectedCount}</div>
      </div> */}

      {/* Search Input */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by Author Name or Email"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>

      {/* Filter Radio Buttons */}
      <div className="mb-3 d-flex flex-wrap gap-3">
        {['all', 'approved', 'notapproved', 'pending', 'rejected'].map((status) => (
          <label key={status} className="me-3">
            <input
              type="radio"
              name="status"
              value={status}
              checked={filter === status}
              onChange={() => handleFilterChange(status)}
            />{' '}
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </label>
        ))}
      </div>

      {/* Table */}
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Author Name</th>
            <th>Email</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {paginatedAuthors.length > 0 ? (
            paginatedAuthors.map((author) => (
              <tr key={author.AuthorID}>
                <td>{author.AuthorName}</td>
                <td>{author.EmailID}</td>
                <td>{getAuthorStatus(author)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3" className="text-center">No authors found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="d-flex justify-content-between mt-3">
        <button
          className="btn btn-secondary"
          onClick={() => handlePageChange(Math.max(page - 1, 1))}
          disabled={page === 1}
        >
          Previous
        </button>
        <span>Page {page}</span>
        <button
          className="btn btn-secondary"
          onClick={() => handlePageChange(page + 1)}
          disabled={page * perPage >= filteredAuthors.length}
        >
          Next
        </button>
      </div>

      {/* Export Buttons */}
      {filteredAuthors.length > 0 && (
        <div className="mt-3">
          <button className="btn btn-success me-2" onClick={exportToPDF}>
            Export to PDF
          </button>
          <button className="btn btn-outline-primary me-2" onClick={exportToExcel}>
            Export to Excel
          </button>

          {/* 🆕 Send Email Button */}
          <button
            className="btn btn-warning"
            onClick={handleSendEmails}
            disabled={isSending}
          >
            {isSending ? 'Sending Emails...' : 'Send Email to All Approved Authors'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthorsList;
